<script setup lang="ts">
import { computed, ref, useId } from 'vue';
import { onClickOutside, onKeyStroke } from '@vueuse/core';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid';
import { useInjectGameEngine, type HighScoreDisqualifyReason } from '@/composables/useGameEngine';

const gameEngine = useInjectGameEngine();
const popoverRef = ref<HTMLElement>();
const triggerRef = ref<HTMLElement>();
const isOpen = ref(false);
const titleId = useId();

const REASON_LABELS: Record<HighScoreDisqualifyReason, string> = {
  speed: 'Playback was below full speed',
  loop: 'A loop section was active',
  paused: 'Playback was paused mid-song',
};

const reasonsList = computed(() =>
  Array.from(gameEngine.highscoreDisqualifyReasons.value).map((r) => REASON_LABELS[r]),
);

const hasReasons = computed(() => reasonsList.value.length > 0);

onClickOutside(popoverRef, () => { isOpen.value = false; }, { ignore: [triggerRef] });
onKeyStroke('Escape', () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  triggerRef.value?.focus();
});
</script>

<template>
  <span class="indicator-slot">
    <Transition name="indicator-fade">
      <button
        v-if="hasReasons"
        ref="triggerRef"
        type="button"
        class="trigger"
        :aria-label="isOpen ? 'Hide highscore notice' : 'Highscore won’t be recorded — show details'"
        :aria-expanded="isOpen"
        aria-haspopup="dialog"
        @click="isOpen = !isOpen"
      >
        <ExclamationTriangleIcon class="trigger-icon" />
      </button>
    </Transition>

    <Transition name="popover">
      <div
        v-if="isOpen && hasReasons"
        ref="popoverRef"
        role="dialog"
        :aria-labelledby="titleId"
        class="popover"
      >
        <p :id="titleId" class="popover-title">Highscore won’t be recorded</p>
        <p class="popover-lede">To set a highscore, finish a full unbroken run at full speed:</p>
        <ul class="reason-list">
          <li v-for="(reason, idx) in reasonsList" :key="idx" class="reason-item">
            <span class="reason-marker" aria-hidden="true" />
            <span>{{ reason }}</span>
          </li>
        </ul>
      </div>
    </Transition>
  </span>
</template>

<style scoped>
/* Reserved-space slot: occupies a stable 2rem × 2rem cell whether or not the
   trigger is visible. Layout cannot shift on reason add/remove. */
.indicator-slot {
  position: relative;
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  /* Shared amber palette — passes WCAG AA on the dark status bar (#000F25)
     and on the popover surface (#0D254F) at every step below. */
  --amber-500: #f59e0b;
  --amber-400: #fbbf24;
  --amber-600: #d97706;
  --amber-on-amber: #1f1300;
}

.trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background: var(--amber-500);
  color: var(--amber-on-amber);
  cursor: pointer;
  transition: background-color 120ms ease, transform 120ms ease;
}

.trigger:hover { background: var(--amber-400); }
.trigger:active { background: var(--amber-600); transform: scale(0.96); }
.trigger:focus-visible {
  outline: 2px solid var(--amber-400);
  outline-offset: 2px;
}

.trigger-icon {
  width: 1rem;
  height: 1rem;
}

.popover {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  z-index: 30;
  width: 18rem;
  padding: 0.75rem 0.875rem 0.875rem;
  border-radius: 0.625rem;
  background: var(--color-surface);
  color: var(--color-text-primary);
  box-shadow:
    0 14px 40px -10px rgba(0, 0, 0, 0.55),
    0 2px 8px rgba(0, 0, 0, 0.35);
  border-left: 3px solid var(--amber-500);
}

.popover-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 0.25rem;
}

.popover-lede {
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--color-text-primary);
  opacity: 0.85;
  margin: 0 0 0.625rem;
}

.reason-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.reason-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--color-text-primary);
}

.reason-marker {
  flex-shrink: 0;
  width: 0.375rem;
  height: 0.375rem;
  margin-top: 0.4rem;
  border-radius: 9999px;
  background: var(--amber-400);
}

/* Fade in/out the trigger without animating its bounding box. */
.indicator-fade-enter-active,
.indicator-fade-leave-active {
  transition: opacity 140ms ease, transform 140ms ease;
}
.indicator-fade-enter-from,
.indicator-fade-leave-to {
  opacity: 0;
  transform: scale(0.85);
}

.popover-enter-active,
.popover-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}
.popover-enter-from,
.popover-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
